// The mobile bottom bar, as data.
//
// #167 D4/D5/D11/D12, Session 78l. This module exists because two screens now
// need the same answer and they must not derive it twice. Sidebar.js renders
// the bar; Company settings -> Navigation shows an administrator what each role
// gets. A settings screen that describes the bar from its own copy of the table
// is a second source of truth, and the first time someone edits one and not the
// other, the screen teaching forty Dallas onboards is the one that lies.
//
// Icons deliberately do NOT live here. They are a rendering concern, they pull
// a dependency this module has no other use for, and keeping them out is what
// lets a settings page import the shape without importing the bar.

// ── SLOTS ────────────────────────────────────────────────────────────────────
//
// #167 D11/D12, Session 78j -- the default bar carries the person's job, not
// the app's build order.
//
// Tools was slot one only because it was the first module built, which is the
// same accident that made Fleet Inventory the landing page -- the entire reason
// #167's gate exists. Fixing the front door and leaving the bar in build order
// would have fixed half the problem. Stock leads for every role: Inventory is
// what goes live on September 1 and it is the only module every role transacts
// in. Tools moves to the far edge.
//
// D12 -- a slot must lead somewhere, and Safety is GONE from here. Its button
// called showComingSoon(): a toast, not a destination, occupying one of five
// capped slots. It rejoins automatically when the module ships, and the rule is
// stated generally so Work Orders arrives on the same terms.
//
// D6, as overturned by Colby at 78f -- Insights goes to every role that can
// reach Insights, not to the executive alone.
export const BAR_SLOTS = {
  stock:     { page: 'inv_home',              module: 'inventory',  label: 'Stock' },
  requests:  { page: 'requests_inbox',        module: 'inventory',  label: 'Requests', badge: true },
  // #391 D8, Session 78n. Deliberately a SEPARATE slot from `requests` rather
  // than a role-conditional page on it. They are different screens answering
  // different questions -- one is the branch's approval queue, one is the
  // person's own work -- and a slot whose destination changes by role is the
  // shape that made #358's badge count a queue its reader could not open.
  //
  // No badge. A requester is not being asked to act on anything here; the
  // notification already told them what changed, and a count on a tracking
  // screen would be a number nobody can drive to zero.
  my_requests: { page: 'my_requests',         module: 'inventory',  label: 'My requests' },
  buying:    { page: 'purchasing_queue',      module: 'purchasing', label: 'Buying' },
  insights:  { page: 'insights',              module: 'insights',   label: 'Insights' },
  invoicing: { page: 'purchasing_invoicing',  module: 'purchasing', label: 'Invoicing' },
  tools:     { page: 'fleet',                 module: 'tools',      label: 'Tools' },
  // #746, Session 79.22b.6.3 (746 D24) -- Houston's welders' page. Placed by
  // default for anyone who records welds at a branch that welds (barForRole()),
  // reachable only where the page rule lets the person in (reachableWith()).
  welding:   { page: 'welding',               module: 'inventory',  label: 'Welding' },
};

// D5 -- Apple HIG and Material both say three to five items for a fixed,
// non-scrolling bar. Scan is one of the five and is not configurable, so four
// destinations is the ceiling. A cap that can be exceeded is not a cap, so this
// is enforced in barForRole() rather than documented in a settings screen.
export const BAR_CAP = 5;
export const BAR_DESTINATION_CAP = BAR_CAP - 1;

// ── ROLE DEFAULTS ────────────────────────────────────────────────────────────
//
// Slots 1-2 sit left of Scan, slots 4-5 right of it. Scan is always three.
//
// team_member is deliberately THREE destinations, and that is a finding rather
// than an omission. #167's gate gave them Requests with a footnote asking the
// build session to confirm the premise rather than trust it -- and the premise
// is false: RequestsInboxPage filters by BRANCH, not by requester, so a team
// member would land in the branch-wide approval queue rather than on "my
// requests". The footnote's own fallback applied: the slot became Tools.
//
// #391, Session 78l -- the premise is worse than that footnote found, and the
// correction belongs here because this is the table it governs.
// isBranchScopedManager is `isManager && !isAdmin && !isExecutive`, so it is
// FALSE for a team_member and load() applies no branch filter at all: they see
// every pending and ordered request at EVERY branch, not merely their own.
//
// The competitive analysis run at 78l settled the shape, and every system read
// gives a requester their own destination rather than a filter on the approval
// queue -- SAP Fiori's My Purchase Requisitions is its own app, ServiceNow
// splits My Requests from a My Approvals portlet, Maximo has a My Requests pod,
// Zoho Expense files it under My View. None of them shows a non-approver the
// shared approval queue. The deciding mechanic is local though: load() fetches
// `.in('status', ['pending','ordered'])`, and a requester's most important row
// is a DENIED one, which that fetch cannot return. A tab there would be a
// second query wearing a first query's clothes.
//
// So the team_member bar has ONE more destination coming, not two, and the
// final arrangement is settled rather than open:
//
//     Stock . My requests . [Scan] . Safety . Tools
//
// My requests lands with its page at Session 78n. Safety takes the remaining
// right-hand slot when the module ships -- Colby's ruling at 78l, and it is the
// daily-inspection portal for forklifts, table saws, the powder-coating oven,
// the spray booth and respirators, so it is the one module a field user opens
// on a schedule rather than on an errand. Five slots, at the cap.
//
// Until 78n the right side pads one cell. That blank is RESERVED rather than
// incidental, which is the difference between a gap and a mistake.
//
// Keyed on role rather than on the role booleans, and that is not a style
// choice. AuthContext derives `isManager` as `role === 'manager' || isAdmin`,
// so an admin satisfies BOTH -- an if/else-if chain testing isManager before
// admin silently gave Colby the branch manager's bar. Caught at 78j by
// measuring the rendered bar rather than by reading, which is the whole
// argument for locked rule 8's "read the predicate, do not infer it".
export const ROLE_BARS = {
  // #391, Session 78n -- My requests lands, exactly as the block above said it
  // would at 78n. The bar is now Stock . My requests . [Scan] . Tools, and the
  // right side pads ONE cell, which is Safety's reserved slot. That blank is
  // still reserved rather than incidental: Safety takes it when the module
  // ships and the bar a person learns in September is the bar they keep.
  //
  // Scan stays centred because BottomNav pads the shorter side to match the
  // longer one (#372) -- left holds two, right holds one and renders a third
  // empty cell. Verified rather than assumed: that padding is what makes this
  // arrangement legal without re-centring anything.
  team_member: { left: ['stock', 'my_requests'], right: ['tools'] },
  executive:   { left: ['stock', 'insights'], right: ['buying', 'tools'] },
  purchasing:  { left: ['stock', 'buying'],   right: ['insights', 'invoicing'] },
  manager:     { left: ['stock', 'requests'], right: ['insights', 'tools'] },
  admin:       { left: ['stock', 'buying'],   right: ['insights', 'tools'] },
};

// Plain English for a settings screen. The role names are the five locked ones
// (CLAUDE.md rule 6) and mean nothing to a person reading a table, so each one
// carries the job rather than the identifier. Deliberately NOT people's names,
// though #167's gate drew them that way: Jennifer is not the purchasing role,
// she currently holds it.
export const ROLE_LABELS = {
  team_member: { name: 'Team member', gloss: 'Installer, fabricator, field crew' },
  manager:     { name: 'Manager',     gloss: 'Branch manager' },
  purchasing:  { name: 'Purchasing',  gloss: 'Sourcing, ordering, invoicing' },
  executive:   { name: 'Executive',   gloss: 'Oversight across branches' },
  admin:       { name: 'Administrator', gloss: 'Full access, settings owner' },
};

export const ROLE_ORDER = ['team_member', 'manager', 'purchasing', 'executive', 'admin'];

// ── RESOLUTION ───────────────────────────────────────────────────────────────

// A default is only ever offered if the person can actually reach it. The
// arrangement above is the intent; this is the guarantee. Callout C's rule from
// the landing-page half, applied to the bar: offering a destination that then
// refuses to load is worse than not offering it.
//
// `caps` is passed rather than derived so this module never imports the
// permission predicates -- the call site already computed them for its own
// rendering, and computing them twice is how the two answers drift.
function reachableWith(caps) {
  return key => {
    if (key === 'buying') return !!caps.canSeePurchasing;
    if (key === 'insights') return !!caps.canSeeInsights;
    if (key === 'invoicing') return !!caps.canSeeInvoicing;
    // #746 -- canSeeWelding(): the person's branch welds, or they are an admin
    // or an executive (746 D5). The first slot that depends on a branch.
    if (key === 'welding') return !!caps.canSeeWelding;
    return true;
  };
}

// Returns { left, right } for a role, unreachable slots removed and the D5 cap
// enforced.
//
// The `purchasing` row asks for Invoicing, which is a capability rather than a
// role: Jennifer and Bridgette are both `purchasing` with opposite
// `invoice_reconciliation_enabled` flags (#289). Rather than encoding that
// fork in the table with a ternary -- which is what made this untestable as
// data -- the row asks for what the job wants and the reachability filter
// removes it for whoever cannot reconcile. `fallback` then keeps the bar from
// losing a slot rather than substituting one, so a purchasing user without the
// capability still gets four destinations.
export function barForRole(role, caps = {}) {
  const base = ROLE_BARS[role] || ROLE_BARS.team_member;
  const ok = reachableWith(caps);
  const fallback = 'tools';

  const resolveSide = keys => {
    const kept = keys.filter(ok);
    // A dropped slot is replaced by Tools -- reachable by every role -- unless
    // Tools is already on the bar, in which case the slot is simply lost and
    // the side pads. Losing one is correct: a bar with the same destination
    // twice is worse than a bar with a gap.
    if (kept.length === keys.length) return kept;
    const alreadyHasTools = keys.includes(fallback) || kept.includes(fallback);
    return alreadyHasTools ? kept : [...kept, fallback];
  };

  let left = resolveSide(base.left);
  let right = resolveSide(base.right);

  // D5, enforced. Trim from the far edge inward, because slot one is the one a
  // thumb reaches first and the far edge is the one a role can most afford to
  // lose. This cannot fire on any of the five role defaults above and is here
  // for the personal bars D4 introduces, where a person picks the contents.
  while (left.length + right.length > BAR_DESTINATION_CAP) {
    if (right.length >= left.length) right = right.slice(0, -1);
    else left = left.slice(0, -1);
  }

  // #746 D24, Session 79.22b.6.3 -- WELDING, AFTER STOCK, FOR WHOEVER RECORDS
  // WELDS AT A BRANCH THAT WELDS, WHERE THE BAR HAS ROOM. The bar can only be
  // reordered, never added to (D4), so this is the default rather than a
  // setting: no setting is required (D10) and the default bar carries the
  // person's job (D11). A team member's bar has a free fourth place -- the one
  // held for Safety above -- and Welding takes it; managers, purchasing, admins
  // and executives already hold four and do not weld. It then re-splits around
  // Scan the way My profile writes it (the left side takes the odd one), which
  // is exactly the drawn bar: Stock . Welding . [Scan] . My requests . Tools.
  if (caps.canRecordWeldsHere && caps.canSeeWelding) {
    const order = [...left, ...right];
    if (order.length < BAR_DESTINATION_CAP && !order.includes('welding')) {
      order.splice(order.indexOf('stock') + 1, 0, 'welding');
      const split = Math.ceil(order.length / 2);
      left = order.slice(0, split);
      right = order.slice(split);
    }
  }

  return { left, right };
}

// The bar a settings screen shows for a role, in reading order, as labels.
// Scan is included because it is on the bar the person sees, and omitting it
// would make the settings screen describe a four-item bar that renders as five.
export function barLabelsForRole(role, caps = {}) {
  const { left, right } = barForRole(role, caps);
  return [
    ...left.map(k => BAR_SLOTS[k]?.label).filter(Boolean),
    'Scan',
    ...right.map(k => BAR_SLOTS[k]?.label).filter(Boolean),
  ];
}

// The bar an individual actually gets: their own arrangement if they have made
// one, otherwise their role's.
//
// #167 D1's two layers, and the resolution order is the same one landingPageFor()
// uses for the front door -- personal choice, then role default. Keeping the two
// features on one order is worth more than it looks: a person who has learned
// that "My profile beats the role" for their landing page does not have to learn
// a second rule for their bar.
//
// A stored bar is FILTERED against the role's own destinations rather than
// trusted, because two things move underneath it. An administrator can change
// what a role's bar holds, and a person's role can change -- either leaves a
// stored key that no longer belongs. Rendering it unfiltered would put an icon
// on the bar that the reachability filter then drops, so the phone would show
// four slots where the settings screen promised five. A stored bar that no
// longer covers the role's destinations exactly is discarded whole rather than
// patched: a half-honoured arrangement is more confusing than the default.
export function resolveBar(profile, caps = {}) {
  const roleBar = barForRole(profile?.role, caps);
  const stored = profile?.bottom_bar;
  if (!stored || !Array.isArray(stored.left) || !Array.isArray(stored.right)) return roleBar;

  const roleKeys = [...roleBar.left, ...roleBar.right];
  const storedKeys = [...stored.left, ...stored.right].filter(k => roleKeys.includes(k));
  const covers = keys => storedKeys.length === keys.length && keys.every(k => storedKeys.includes(k));
  if (!covers(roleKeys)) {
    // #746 D24 -- the one patch, and it is deliberate. A person who arranged
    // their bar before Welding reached it is missing ONLY Welding: keep their
    // order and add Welding after Stock, rather than throwing an arrangement
    // away because Stede added a page. Anything else still falls back whole.
    const withoutWelding = roleKeys.filter(k => k !== 'welding');
    if (withoutWelding.length === roleKeys.length || !covers(withoutWelding)) return roleBar;
    storedKeys.splice(storedKeys.indexOf('stock') + 1, 0, 'welding');
  }

  // Scan sits between the two sides, so the split is where it lands. The left
  // side takes the extra slot on an odd count -- slot one is the one a thumb
  // reaches without moving, and the far right is the one it reaches last. This
  // matches the split My Profile writes, and the two must agree or a saved
  // arrangement would render differently from the screen that saved it.
  const split = Math.ceil(storedKeys.length / 2);
  return { left: storedKeys.slice(0, split), right: storedKeys.slice(split) };
}
